import React from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';
import moment from 'moment';

import { LbSelect } from '@infoods/lifebase-ui';
import classes from './DetailedRevenue.module.scss';
import { PageTitle } from '../../../shared/components/ui-components';

import { DateRangePicker } from '../../../shared/components/form-components';
import { PaymentHistoryList } from './PaymentHistoryList/PaymentHistoryList';
import { RevenuePerformanceSummary } from './RevenuePerformanceSummary/RevenuePerformanceSummary';
import { useAutoSubmitForm } from '../../../shared/hooks/useAutoSubmitForm';
import { RevenueGeneralFilterFormValues } from './DetailedRevenue.settings';
import { useRevenueGeneralFilter } from './hooks/useRevenueGeneralFilter';
import { dateMonthFormat, oldDateFormat } from '../../../shared/constants/apiDateFormat';
import { getDefaultDateRange } from './utils/getDefaultDateRange';
import { getFormattedDate } from '../../../shared/utils/dateUtils';
import { useCoachOptions } from './hooks/useCoachOptions';

type DetailedRevenueProps = {
  entityId: number;
  entityRegistrationDate: string | null; // oldDateFormat
};
/**
 * Date range filter works with month range but api works with date range
 * so we need to convert month range to date range. useRevenueGeneralFilter hook is used to convert month range to date range.
 */
const DetailedRevenue = observer(
  ({ entityId, entityRegistrationDate }: DetailedRevenueProps) => {
    const coachOptions = useCoachOptions(entityId);
    const defaultDateRange = getDefaultDateRange(entityRegistrationDate);
    const defaultGeneralFilters: RevenueGeneralFilterFormValues = {
      ...defaultDateRange,
      coachId: '',
    };

    const { control, watch, handleSubmit } = useForm<RevenueGeneralFilterFormValues>({
      shouldFocusError: false,
      mode: 'onBlur',
      defaultValues: defaultGeneralFilters,
    });

    const { generalFilters, applyGeneralFilters } =
      useRevenueGeneralFilter(defaultGeneralFilters);

    useAutoSubmitForm(handleSubmit(applyGeneralFilters), watch);

    const minDate = entityRegistrationDate
      ? moment(getFormattedDate(entityRegistrationDate, dateMonthFormat, oldDateFormat))
      : undefined;

    const inputGeneralProps = { control, variant: 'outlined', size: 'medium' } as const;

    return (
      <div>
        <PageTitle title="Detailed Revenue">
          <div className={classes.filters}>
            <LbSelect
              {...inputGeneralProps}
              label="Coach"
              displayEmpty
              name="coachId"
              options={coachOptions}
            />
            <DateRangePicker
              {...inputGeneralProps}
              mode="month"
              className={classes.dateRangePicker}
              startPickerProps={{
                name: 'firstMonth',
                label: 'First month',
                rules: { required: 'Start date is required' },
                minDate,
              }}
              endPickerProps={{
                name: 'lastMonth',
                label: 'Last month',
                disableFuture: true,
                rules: { required: 'End date is required' },
              }}
              dataTestId="date-range-picker"
            />
          </div>
        </PageTitle>
        <RevenuePerformanceSummary entityId={entityId} generalFilters={generalFilters} />
        <PaymentHistoryList entityId={entityId} generalFilters={generalFilters} />
      </div>
    );
  },
);

export { DetailedRevenue };
